// Step 6 of the client-side X12 pipeline: checks the envelope structure and,
// for each 850, 852, 855, 856 or 997, its loop content against the dictionary — and
// returns a flat list of advisory findings. This module is advisory-only *by
// construction*: nothing in its public API returns a boolean "can export"
// gate, so nothing downstream can accidentally block export on a finding it
// produces.

import type {EdiElementValue, EdiSegment} from './tokenizer';
import {getSegmentDefinition} from './dictionary';
import type {ItemLoop850, NameLoop850, Parsed850} from './parser850';
import type {ItemActivityLoop852, NameLoop852, Parsed852} from './parser852';
import type {ItemAckLoop855, NameLoop855, Parsed855} from './parser855';
import type {HLNode856, NameLoop856, Parsed856} from './parser856';
import type {Parsed997} from './parser997';
import type {ItemLoop840, NameLoop840, Parsed840} from './parser840';
import type {ItemLoop810, NameLoop810, Parsed810} from './parser810';
import type {
  ItemLoop843,
  ItemNameSubLoop843,
  NameLoop843,
  Parsed843,
} from './parser843';
import type {ItemLoop844, NameLoop844, Parsed844} from './parser844';
import {validateParsed845} from './validator845';
import {validateParsed867} from './validator867';
import {validateParsed210} from './validator210';
import type {EdiEnvelopeExplained} from './explainedEnvelope';

export type FindingSeverity = 'error' | 'warning';

export interface ValidationFinding {
  severity: FindingSeverity;
  /** Identifies exactly which segment/element this finding is about, e.g.
   * "groups[0].transactions[0].header.beg" — stable enough for the UI to
   * match a finding back to the field it renders. */
  path: string;
  message: string;
}

function elementValueAsString(
  value: EdiElementValue | undefined
): string | undefined {
  if (value === undefined) return undefined;
  return Array.isArray(value) ? value.join('') : value;
}

function pad2(position: number): string {
  return String(position).padStart(2, '0');
}

/** Spec position, label and fixed width, in characters, of each of
 * ISA01-ISA15 (ISA16 is the sub-element separator itself, not a data
 * element, so it's excluded). */
const ISA_ELEMENT_SPEC: Array<[number, string, number]> = [
  [1, 'Authorization Information Qualifier', 2],
  [2, 'Authorization Information', 10],
  [3, 'Security Information Qualifier', 2],
  [4, 'Security Information', 10],
  [5, 'Interchange ID Qualifier', 2],
  [6, 'Interchange Sender ID', 15],
  [7, 'Interchange ID Qualifier', 2],
  [8, 'Interchange Receiver ID', 15],
  [9, 'Interchange Date', 6],
  [10, 'Interchange Time', 4],
  [11, 'Interchange Control Standards Identifier', 1],
  [12, 'Interchange Control Version Number', 5],
  [13, 'Interchange Control Number', 9],
  [14, 'Acknowledgment Requested', 1],
  [15, 'Usage Indicator', 1],
];

/** Pushes a low-severity warning for each ISA01-ISA15 element whose actual
 * length doesn't match the X12 spec's fixed width — tolerated by `tokenize`
 * but worth flagging, not blocking. */
function checkIsaPadding(
  isaSegment: EdiSegment,
  findings: ValidationFinding[]
): void {
  for (const [position, label, width] of ISA_ELEMENT_SPEC) {
    const value = elementValueAsString(isaSegment.elements[position - 1]);
    if (value === undefined) continue;
    if (value.length !== width) {
      findings.push({
        severity: 'warning',
        path: 'isaSegment',
        message: `ISA${pad2(position)} (${label}) is ${value.length} character(s) — the X12 spec fixes this field at ${width}.`,
      });
    }
  }
}

const NUMERIC_PATTERN = /^-?\d+(\.\d+)?$/;

/** True when `value` is all digits of the right length for `format`
 * (YYMMDD, else CCYYMMDD) and names a real calendar day — so "20261301"
 * (month 13) or "20260230" is rejected, not just non-digits. */
function isCalendarDate(value: string, format: string | undefined): boolean {
  const short = format === 'YYMMDD';
  const match = (
    short ? /^(\d{2})(\d{2})(\d{2})$/ : /^(\d{4})(\d{2})(\d{2})$/
  ).exec(value);
  if (!match) return false;
  const year = short ? 2000 + Number(match[1]) : Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

/** Checks every dictionary-known element of one segment: required-but-empty,
 * not a member of a fixed code list, or not shaped like its declared
 * date/numeric format. Every check here is advisory — a mismatch against
 * this tool's own (necessarily partial) code lists is reported as a warning,
 * not an error, since the dictionary is generic and may not carry every code
 * a real partner uses. */
export function checkElements(
  segment: EdiSegment,
  path: string,
  findings: ValidationFinding[]
): void {
  const definition = getSegmentDefinition(segment.id);

  for (const elementDef of definition.elements) {
    const elementPath = `${path}.elements[${elementDef.position - 1}]`;
    const label = `${segment.id}${pad2(elementDef.position)} (${elementDef.label})`;
    const value = elementValueAsString(
      segment.elements[elementDef.position - 1]
    );

    if (elementDef.required && (value === undefined || value.trim() === '')) {
      findings.push({
        severity: 'error',
        path: elementPath,
        message: `${label} is required but missing.`,
      });
      continue;
    }

    if (value === undefined) continue;
    // Trimmed once and reused by every check below, so a purely
    // whitespace-related formatting difference (incidental surrounding
    // whitespace, or an element that's whitespace-only) never by itself
    // produces a finding; each finding message below still shows the
    // original, untrimmed value as it appeared in the document.
    const trimmedValue = value.trim();
    if (trimmedValue === '') continue;

    if (elementDef.codes && elementDef.codes.length > 0) {
      // ZZ ("Mutually Defined") is a legitimate catch-all by design — its
      // meaning is whatever the trading partners agreed to out of band, so
      // no code-list-checked element anywhere in the dictionary should ever
      // produce an "unrecognized code" finding for it. This is unconditional
      // — it applies regardless of whether this specific field's own code
      // list defines ZZ as "Mutually Defined" (or lists ZZ at all) — rather
      // than only exempting fields whose list happens to already include
      // it. ZZ still renders with its normal label/tooltip wherever a code
      // list does define one for it; only this finding is suppressed.
      //
      // The comparison trims the document value first so a code that's
      // genuinely present in the list isn't misflagged purely because of
      // incidental surrounding whitespace in the source document, and
      // case-folds so a lowercase/mixed-case value (e.g. 'vn') matches the
      // list's 'VN' the same way lib/dictionary/index.ts's getCodeLabel and
      // SegmentField's matched-code lookup do — otherwise a value could
      // render its label (from one of those) while this check still flags
      // it as unrecognized. 'zz'/'Zz' is accepted as the same ZZ exemption
      // for the same reason.
      const normalizedValue = trimmedValue.toUpperCase();
      const known =
        normalizedValue === 'ZZ' ||
        elementDef.codes.some(c => c.code.toUpperCase() === normalizedValue);
      if (!known) {
        findings.push({
          severity: 'warning',
          path: elementPath,
          message: `${label} value "${value}" is not one of this dictionary's known codes.`,
        });
      }
    }

    if (elementDef.dataType === 'date') {
      if (!isCalendarDate(trimmedValue, elementDef.format)) {
        findings.push({
          severity: 'warning',
          path: elementPath,
          message: `${label} value "${value}" doesn't look like a ${elementDef.format ?? 'CCYYMMDD'} date.`,
        });
      }
    }

    if (
      elementDef.dataType === 'numeric' &&
      !NUMERIC_PATTERN.test(trimmedValue)
    ) {
      findings.push({
        severity: 'warning',
        path: elementPath,
        message: `${label} value "${value}" doesn't look like a number.`,
      });
    }
  }
}

export interface PathedSegment {
  segment: EdiSegment;
  path: string;
}

function collectNameLoopSegments(
  loop: NameLoop850,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: loop.n1, path: `${path}.n1`}];
  if (loop.n2) out.push({segment: loop.n2, path: `${path}.n2`});
  if (loop.n3) out.push({segment: loop.n3, path: `${path}.n3`});
  if (loop.n4) out.push({segment: loop.n4, path: `${path}.n4`});
  if (loop.n5) out.push({segment: loop.n5, path: `${path}.n5`});
  if (loop.n6) out.push({segment: loop.n6, path: `${path}.n6`});
  loop.n9.forEach((s, i) => out.push({segment: s, path: `${path}.n9[${i}]`}));
  loop.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  if (loop.per) out.push({segment: loop.per, path: `${path}.per`});
  return out;
}

function collectItemSegments(item: ItemLoop850, path: string): PathedSegment[] {
  const out: PathedSegment[] = [{segment: item.po1, path: `${path}.po1`}];
  item.mea.forEach((s, i) => out.push({segment: s, path: `${path}.mea[${i}]`}));
  item.pid.forEach((s, i) => out.push({segment: s, path: `${path}.pid[${i}]`}));
  item.pwk.forEach((s, i) => out.push({segment: s, path: `${path}.pwk[${i}]`}));
  item.pkg.forEach((s, i) => out.push({segment: s, path: `${path}.pkg[${i}]`}));
  if (item.po3) out.push({segment: item.po3, path: `${path}.po3`});
  if (item.ctp) out.push({segment: item.ctp, path: `${path}.ctp`});
  item.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  if (item.per) out.push({segment: item.per, path: `${path}.per`});
  item.sac.forEach((s, i) => out.push({segment: s, path: `${path}.sac[${i}]`}));
  if (item.it8) out.push({segment: item.it8, path: `${path}.it8`});
  if (item.ctb) out.push({segment: item.ctb, path: `${path}.ctb`});
  if (item.tax) out.push({segment: item.tax, path: `${path}.tax`});
  if (item.fob) out.push({segment: item.fob, path: `${path}.fob`});
  item.sdq.forEach((s, i) => out.push({segment: s, path: `${path}.sdq[${i}]`}));
  item.dtm.forEach((s, i) => out.push({segment: s, path: `${path}.dtm[${i}]`}));
  if (item.ldt) out.push({segment: item.ldt, path: `${path}.ldt`});
  if (item.iss) out.push({segment: item.iss, path: `${path}.iss`});
  item.pam.forEach((s, i) => out.push({segment: s, path: `${path}.pam[${i}]`}));
  item.msg.forEach((s, i) => out.push({segment: s, path: `${path}.msg[${i}]`}));
  if (item.sln) out.push({segment: item.sln.sln, path: `${path}.sln.sln`});
  item.schedule.forEach((s, i) =>
    out.push({segment: s, path: `${path}.schedule[${i}]`})
  );
  item.csh.forEach((s, i) => out.push({segment: s, path: `${path}.csh[${i}]`}));
  item.tc2.forEach((s, i) => out.push({segment: s, path: `${path}.tc2[${i}]`}));
  item.pct.forEach((s, i) => out.push({segment: s, path: `${path}.pct[${i}]`}));
  if (item.transportation) {
    out.push({
      segment: item.transportation.tc1,
      path: `${path}.transportation.tc1`,
    });
  }
  item.subNameLoops.forEach((sub, i) => {
    const subPath = `${path}.subNameLoops[${i}]`;
    out.push({segment: sub.n1, path: `${subPath}.n1`});
    if (sub.n2) out.push({segment: sub.n2, path: `${subPath}.n2`});
    if (sub.n3) out.push({segment: sub.n3, path: `${subPath}.n3`});
    if (sub.n4) out.push({segment: sub.n4, path: `${subPath}.n4`});
  });
  return out;
}

function collectNameLoopSegments840(
  loop: NameLoop840,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: loop.n1, path: `${path}.n1`}];
  if (loop.n2) out.push({segment: loop.n2, path: `${path}.n2`});
  if (loop.n3) out.push({segment: loop.n3, path: `${path}.n3`});
  if (loop.n4) out.push({segment: loop.n4, path: `${path}.n4`});
  if (loop.n5) out.push({segment: loop.n5, path: `${path}.n5`});
  if (loop.n6) out.push({segment: loop.n6, path: `${path}.n6`});
  loop.n9.forEach((s, i) => out.push({segment: s, path: `${path}.n9[${i}]`}));
  loop.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  if (loop.per) out.push({segment: loop.per, path: `${path}.per`});
  return out;
}

function collectItemSegments840(
  item: ItemLoop840,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: item.po1, path: `${path}.po1`}];
  item.pid.forEach((s, i) => out.push({segment: s, path: `${path}.pid[${i}]`}));
  item.mea.forEach((s, i) => out.push({segment: s, path: `${path}.mea[${i}]`}));
  if (item.ctp) out.push({segment: item.ctp, path: `${path}.ctp`});
  item.sac.forEach((s, i) => out.push({segment: s, path: `${path}.sac[${i}]`}));
  item.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  if (item.per) out.push({segment: item.per, path: `${path}.per`});
  item.sdq.forEach((s, i) => out.push({segment: s, path: `${path}.sdq[${i}]`}));
  item.dtm.forEach((s, i) => out.push({segment: s, path: `${path}.dtm[${i}]`}));
  item.subNameLoops.forEach((sub, i) => {
    const subPath = `${path}.subNameLoops[${i}]`;
    out.push({segment: sub.n1, path: `${subPath}.n1`});
    if (sub.n2) out.push({segment: sub.n2, path: `${subPath}.n2`});
    if (sub.n3) out.push({segment: sub.n3, path: `${subPath}.n3`});
    if (sub.n4) out.push({segment: sub.n4, path: `${subPath}.n4`});
  });
  return out;
}

function collectParsedSegments840(
  parsed: Parsed840,
  basePath: string
): PathedSegment[] {
  const out: PathedSegment[] = [];
  const {header} = parsed;
  const push = (segment: EdiSegment | undefined, suffix: string) => {
    if (segment) out.push({segment, path: `${basePath}.${suffix}`});
  };
  const pushAll = (segments: EdiSegment[], suffix: string) => {
    segments.forEach((s, i) =>
      out.push({segment: s, path: `${basePath}.${suffix}[${i}]`})
    );
  };

  push(header.bqt, 'header.bqt');
  push(header.cur, 'header.cur');
  pushAll(header.refs, 'header.refs');
  push(header.per, 'header.per');
  push(header.fob, 'header.fob');
  push(header.itd, 'header.itd');
  pushAll(header.dtm, 'header.dtm');
  push(header.tax, 'header.tax');
  pushAll(header.msg, 'header.msg');

  parsed.nameLoops.forEach((loop, i) => {
    out.push(
      ...collectNameLoopSegments840(loop, `${basePath}.nameLoops[${i}]`)
    );
  });
  parsed.items.forEach((item, i) => {
    out.push(...collectItemSegments840(item, `${basePath}.items[${i}]`));
  });

  push(parsed.summary.ctt, 'summary.ctt');
  pushAll(parsed.summary.amt, 'summary.amt');

  return out;
}

function collectNameLoopSegments843(
  loop: NameLoop843,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: loop.n1, path: `${path}.n1`}];
  if (loop.n2) out.push({segment: loop.n2, path: `${path}.n2`});
  if (loop.n3) out.push({segment: loop.n3, path: `${path}.n3`});
  if (loop.n4) out.push({segment: loop.n4, path: `${path}.n4`});
  if (loop.n5) out.push({segment: loop.n5, path: `${path}.n5`});
  if (loop.n6) out.push({segment: loop.n6, path: `${path}.n6`});
  loop.n9.forEach((s, i) => out.push({segment: s, path: `${path}.n9[${i}]`}));
  loop.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  if (loop.per) out.push({segment: loop.per, path: `${path}.per`});
  return out;
}

function collectItemSegments843(
  item: ItemLoop843,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: item.po1, path: `${path}.po1`}];
  item.pid.forEach((s, i) => out.push({segment: s, path: `${path}.pid[${i}]`}));
  item.mea.forEach((s, i) => out.push({segment: s, path: `${path}.mea[${i}]`}));
  if (item.ctp) out.push({segment: item.ctp, path: `${path}.ctp`});
  item.sac.forEach((s, i) => out.push({segment: s, path: `${path}.sac[${i}]`}));
  item.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  if (item.per) out.push({segment: item.per, path: `${path}.per`});
  item.sdq.forEach((s, i) => out.push({segment: s, path: `${path}.sdq[${i}]`}));
  item.dtm.forEach((s, i) => out.push({segment: s, path: `${path}.dtm[${i}]`}));
  item.subNameLoops.forEach((sub: ItemNameSubLoop843, i) => {
    const subPath = `${path}.subNameLoops[${i}]`;
    out.push({segment: sub.n1, path: `${subPath}.n1`});
    if (sub.n2) out.push({segment: sub.n2, path: `${subPath}.n2`});
    if (sub.n3) out.push({segment: sub.n3, path: `${subPath}.n3`});
    if (sub.n4) out.push({segment: sub.n4, path: `${subPath}.n4`});
  });
  return out;
}

function collectParsedSegments843(
  parsed: Parsed843,
  basePath: string
): PathedSegment[] {
  const out: PathedSegment[] = [];
  const {header} = parsed;
  const push = (segment: EdiSegment | undefined, suffix: string) => {
    if (segment) out.push({segment, path: `${basePath}.${suffix}`});
  };
  const pushAll = (segments: EdiSegment[], suffix: string) => {
    segments.forEach((s, i) =>
      out.push({segment: s, path: `${basePath}.${suffix}[${i}]`})
    );
  };

  push(header.bqr, 'header.bqr');
  push(header.cur, 'header.cur');
  pushAll(header.refs, 'header.refs');
  push(header.per, 'header.per');
  push(header.fob, 'header.fob');
  push(header.itd, 'header.itd');
  pushAll(header.dtm, 'header.dtm');
  pushAll(header.msg, 'header.msg');

  parsed.nameLoops.forEach((loop, i) => {
    out.push(
      ...collectNameLoopSegments843(loop, `${basePath}.nameLoops[${i}]`)
    );
  });
  parsed.items.forEach((item, i) => {
    out.push(...collectItemSegments843(item, `${basePath}.items[${i}]`));
  });

  push(parsed.summary.ctt, 'summary.ctt');
  pushAll(parsed.summary.amt, 'summary.amt');

  return out;
}

function collectNameLoopSegments844(
  loop: NameLoop844,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: loop.n1, path: `${path}.n1`}];
  if (loop.n2) out.push({segment: loop.n2, path: `${path}.n2`});
  if (loop.n3) out.push({segment: loop.n3, path: `${path}.n3`});
  if (loop.n4) out.push({segment: loop.n4, path: `${path}.n4`});
  if (loop.n5) out.push({segment: loop.n5, path: `${path}.n5`});
  if (loop.n6) out.push({segment: loop.n6, path: `${path}.n6`});
  loop.n9.forEach((s, i) => out.push({segment: s, path: `${path}.n9[${i}]`}));
  loop.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  if (loop.per) out.push({segment: loop.per, path: `${path}.per`});
  return out;
}

function collectItemSegments844(
  item: ItemLoop844,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: item.pad, path: `${path}.pad`}];
  item.qty.forEach((s, i) => out.push({segment: s, path: `${path}.qty[${i}]`}));
  item.amt.forEach((s, i) => out.push({segment: s, path: `${path}.amt[${i}]`}));
  if (item.ctp) out.push({segment: item.ctp, path: `${path}.ctp`});
  return out;
}

function collectParsedSegments844(
  parsed: Parsed844,
  basePath: string
): PathedSegment[] {
  const out: PathedSegment[] = [];
  const {header} = parsed;
  const push = (segment: EdiSegment | undefined, suffix: string) => {
    if (segment) out.push({segment, path: `${basePath}.${suffix}`});
  };
  const pushAll = (segments: EdiSegment[], suffix: string) => {
    segments.forEach((s, i) =>
      out.push({segment: s, path: `${basePath}.${suffix}[${i}]`})
    );
  };

  push(header.baa, 'header.baa');
  push(header.con, 'header.con');
  pushAll(header.refs, 'header.refs');
  pushAll(header.dtm, 'header.dtm');

  parsed.nameLoops.forEach((loop, i) => {
    out.push(
      ...collectNameLoopSegments844(loop, `${basePath}.nameLoops[${i}]`)
    );
  });
  parsed.items.forEach((item, i) => {
    out.push(...collectItemSegments844(item, `${basePath}.items[${i}]`));
  });

  return out;
}

function collectNameLoopSegments852(
  loop: NameLoop852,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: loop.n1, path: `${path}.n1`}];
  if (loop.n2) out.push({segment: loop.n2, path: `${path}.n2`});
  if (loop.n3) out.push({segment: loop.n3, path: `${path}.n3`});
  if (loop.n4) out.push({segment: loop.n4, path: `${path}.n4`});
  if (loop.n5) out.push({segment: loop.n5, path: `${path}.n5`});
  if (loop.n6) out.push({segment: loop.n6, path: `${path}.n6`});
  loop.n9.forEach((s, i) => out.push({segment: s, path: `${path}.n9[${i}]`}));
  loop.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  if (loop.per) out.push({segment: loop.per, path: `${path}.per`});
  return out;
}

function collectItemActivitySegments852(
  item: ItemActivityLoop852,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: item.lin, path: `${path}.lin`}];
  item.ctp.forEach((s, i) => out.push({segment: s, path: `${path}.ctp[${i}]`}));
  item.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  if (item.per) out.push({segment: item.per, path: `${path}.per`});
  item.sdq.forEach((s, i) => out.push({segment: s, path: `${path}.sdq[${i}]`}));
  item.qty.forEach((s, i) => out.push({segment: s, path: `${path}.qty[${i}]`}));
  item.dtm.forEach((s, i) => out.push({segment: s, path: `${path}.dtm[${i}]`}));
  item.pid.forEach((s, i) => out.push({segment: s, path: `${path}.pid[${i}]`}));
  item.subNameLoops.forEach((sub, i) => {
    const subPath = `${path}.subNameLoops[${i}]`;
    out.push({segment: sub.n1, path: `${subPath}.n1`});
    if (sub.n2) out.push({segment: sub.n2, path: `${subPath}.n2`});
    if (sub.n3) out.push({segment: sub.n3, path: `${subPath}.n3`});
    if (sub.n4) out.push({segment: sub.n4, path: `${subPath}.n4`});
  });
  return out;
}

function collectParsedSegments852(
  parsed: Parsed852,
  basePath: string
): PathedSegment[] {
  const out: PathedSegment[] = [];
  const {header} = parsed;
  const push = (segment: EdiSegment | undefined, suffix: string) => {
    if (segment) out.push({segment, path: `${basePath}.${suffix}`});
  };
  const pushAll = (segments: EdiSegment[], suffix: string) => {
    segments.forEach((s, i) =>
      out.push({segment: s, path: `${basePath}.${suffix}[${i}]`})
    );
  };

  push(header.bct, 'header.bct');
  pushAll(header.refs, 'header.refs');
  pushAll(header.dtm, 'header.dtm');
  push(header.per, 'header.per');
  push(header.fob, 'header.fob');
  pushAll(header.pwk, 'header.pwk');
  pushAll(header.msg, 'header.msg');

  parsed.nameLoops.forEach((loop, i) => {
    out.push(
      ...collectNameLoopSegments852(loop, `${basePath}.nameLoops[${i}]`)
    );
  });
  parsed.items.forEach((item, i) => {
    out.push(
      ...collectItemActivitySegments852(item, `${basePath}.items[${i}]`)
    );
  });

  push(parsed.summary.ctt, 'summary.ctt');
  pushAll(parsed.summary.amt, 'summary.amt');

  return out;
}

function collectParsedSegments(
  parsed: Parsed850,
  basePath: string
): PathedSegment[] {
  const out: PathedSegment[] = [];
  const {header} = parsed;
  const push = (segment: EdiSegment | undefined, suffix: string) => {
    if (segment) out.push({segment, path: `${basePath}.${suffix}`});
  };
  const pushAll = (segments: EdiSegment[], suffix: string) => {
    segments.forEach((s, i) =>
      out.push({segment: s, path: `${basePath}.${suffix}[${i}]`})
    );
  };

  push(header.beg, 'header.beg');
  push(header.cur, 'header.cur');
  pushAll(header.refs, 'header.refs');
  push(header.per, 'header.per');
  push(header.tax, 'header.tax');
  push(header.fob, 'header.fob');
  push(header.ctp, 'header.ctp');
  push(header.itd, 'header.itd');
  push(header.dis, 'header.dis');
  push(header.inc, 'header.inc');
  pushAll(header.dtm, 'header.dtm');
  push(header.ldt, 'header.ldt');
  pushAll(header.msg, 'header.msg');
  push(header.iss, 'header.iss');
  push(header.pct, 'header.pct');
  pushAll(header.pwk, 'header.pwk');
  pushAll(header.lm, 'header.lm');
  pushAll(header.lq, 'header.lq');

  parsed.nameLoops.forEach((loop, i) => {
    out.push(...collectNameLoopSegments(loop, `${basePath}.nameLoops[${i}]`));
  });
  parsed.items.forEach((item, i) => {
    out.push(...collectItemSegments(item, `${basePath}.items[${i}]`));
  });

  push(parsed.summary.ctt, 'summary.ctt');
  pushAll(parsed.summary.amt, 'summary.amt');

  return out;
}

function collectParsedSegments997(
  parsed: Parsed997,
  basePath: string
): PathedSegment[] {
  const out: PathedSegment[] = [];
  const push = (segment: EdiSegment | undefined, suffix: string) => {
    if (segment) out.push({segment, path: `${basePath}.${suffix}`});
  };

  push(parsed.ak1, 'ak1');

  parsed.ak2Loops.forEach((ak2Loop, i) => {
    const loopPath = `ak2Loops[${i}]`;
    push(ak2Loop.ak2, `${loopPath}.ak2`);
    ak2Loop.ak3Loops.forEach((ak3Loop, j) => {
      const ak3LoopPath = `${loopPath}.ak3Loops[${j}]`;
      push(ak3Loop.ak3, `${ak3LoopPath}.ak3`);
      ak3Loop.ak4.forEach((ak4, k) => {
        out.push({segment: ak4, path: `${basePath}.${ak3LoopPath}.ak4[${k}]`});
      });
    });
    push(ak2Loop.ak5, `${loopPath}.ak5`);
  });

  push(parsed.ak9, 'ak9');

  return out;
}

function validateParsed997(
  parsed: Parsed997,
  txPath: string
): ValidationFinding[] {
  const findings: ValidationFinding[] = [];

  if (!parsed.ak1) {
    findings.push({
      severity: 'error',
      path: `${txPath}.ak1`,
      message:
        'Missing AK1 segment — every 997 must identify the functional group it acknowledges.',
    });
  }

  if (!parsed.ak9) {
    findings.push({
      severity: 'error',
      path: `${txPath}.ak9`,
      message:
        'Missing AK9 segment — the transaction has no functional group response trailer.',
    });
  } else {
    const declaredIncluded = elementValueAsString(parsed.ak9.elements[1]);
    const declaredReceived = elementValueAsString(parsed.ak9.elements[2]);
    const declaredAccepted = elementValueAsString(parsed.ak9.elements[3]);
    const actualCount = parsed.ak2Loops.length;
    const acceptedCount = parsed.ak2Loops.filter(loop => {
      const code = elementValueAsString(loop.ak5?.elements[0])?.toUpperCase();
      return code === 'A' || code === 'E';
    }).length;

    if (
      declaredIncluded !== undefined &&
      Number(declaredIncluded) !== actualCount
    ) {
      findings.push({
        severity: 'error',
        path: `${txPath}.ak9`,
        message: `AK902 says ${declaredIncluded} transaction set(s) included but ${actualCount} AK2 loop(s) were found.`,
      });
    }

    if (
      declaredReceived !== undefined &&
      Number(declaredReceived) !== actualCount
    ) {
      findings.push({
        severity: 'error',
        path: `${txPath}.ak9`,
        message: `AK903 says ${declaredReceived} transaction set(s) received but ${actualCount} AK2 loop(s) were found.`,
      });
    }

    if (
      declaredAccepted !== undefined &&
      Number(declaredAccepted) !== acceptedCount
    ) {
      findings.push({
        severity: 'error',
        path: `${txPath}.ak9`,
        message: `AK904 says ${declaredAccepted} transaction set(s) accepted but ${acceptedCount} AK2 loop(s) with an accepted AK501 code were found.`,
      });
    }
  }

  for (const {segment, path} of collectParsedSegments997(parsed, txPath)) {
    checkElements(segment, path, findings);
  }

  return findings;
}

function validateParsed850(
  parsed: Parsed850,
  txPath: string
): ValidationFinding[] {
  const findings: ValidationFinding[] = [];

  if (!parsed.header.beg) {
    findings.push({
      severity: 'error',
      path: `${txPath}.header.beg`,
      message:
        'Missing BEG segment — every 850 must open with a Beginning Segment for Purchase Order.',
    });
  }

  if (parsed.items.length === 0) {
    findings.push({
      severity: 'error',
      path: `${txPath}.items`,
      message: 'No PO1 line items found in this purchase order.',
    });
  }

  if (!parsed.summary.ctt) {
    findings.push({
      severity: 'error',
      path: `${txPath}.summary.ctt`,
      message:
        'Missing CTT segment — the transaction has no summary line-item count.',
    });
  } else {
    const declared = elementValueAsString(parsed.summary.ctt.elements[0]);
    if (declared !== undefined && Number(declared) !== parsed.items.length) {
      findings.push({
        severity: 'error',
        path: `${txPath}.summary.ctt`,
        message: `CTT01 says ${declared} line item(s) but ${parsed.items.length} PO1 line(s) were found.`,
      });
    }
  }

  for (const {segment, path} of collectParsedSegments(parsed, txPath)) {
    checkElements(segment, path, findings);
  }

  return findings;
}

function validateParsed840(
  parsed: Parsed840,
  txPath: string
): ValidationFinding[] {
  const findings: ValidationFinding[] = [];

  if (!parsed.header.bqt) {
    findings.push({
      severity: 'error',
      path: `${txPath}.header.bqt`,
      message:
        'Missing BQT segment — every 840 must open with a Beginning Segment for Request for Quotation.',
    });
  }

  if (parsed.items.length === 0) {
    findings.push({
      severity: 'error',
      path: `${txPath}.items`,
      message:
        'No PO1 requested line items found in this request for quotation.',
    });
  }

  if (!parsed.summary.ctt) {
    findings.push({
      severity: 'error',
      path: `${txPath}.summary.ctt`,
      message:
        'Missing CTT segment — the transaction has no summary line-item count.',
    });
  } else {
    const declared = elementValueAsString(parsed.summary.ctt.elements[0]);
    if (declared !== undefined && Number(declared) !== parsed.items.length) {
      findings.push({
        severity: 'error',
        path: `${txPath}.summary.ctt`,
        message: `CTT01 says ${declared} line item(s) but ${parsed.items.length} PO1 line(s) were found.`,
      });
    }
  }

  for (const {segment, path} of collectParsedSegments840(parsed, txPath)) {
    checkElements(segment, path, findings);
  }

  return findings;
}

function validateParsed843(
  parsed: Parsed843,
  txPath: string
): ValidationFinding[] {
  const findings: ValidationFinding[] = [];

  if (!parsed.header.bqr) {
    findings.push({
      severity: 'error',
      path: `${txPath}.header.bqr`,
      message:
        'Missing BQR segment — every 843 must open with a Beginning Segment for Response to Request for Quotation.',
    });
  }

  if (parsed.items.length === 0) {
    findings.push({
      severity: 'error',
      path: `${txPath}.items`,
      message:
        'No PO1 quoted line items found in this response to request for quotation.',
    });
  }

  if (!parsed.summary.ctt) {
    findings.push({
      severity: 'error',
      path: `${txPath}.summary.ctt`,
      message:
        'Missing CTT segment — the transaction has no summary line-item count.',
    });
  } else {
    const declared = elementValueAsString(parsed.summary.ctt.elements[0]);
    if (declared !== undefined && Number(declared) !== parsed.items.length) {
      findings.push({
        severity: 'error',
        path: `${txPath}.summary.ctt`,
        message: `CTT01 says ${declared} line item(s) but ${parsed.items.length} PO1 line(s) were found.`,
      });
    }
  }

  for (const {segment, path} of collectParsedSegments843(parsed, txPath)) {
    checkElements(segment, path, findings);
  }

  return findings;
}

function validateParsed844(
  parsed: Parsed844,
  txPath: string
): ValidationFinding[] {
  const findings: ValidationFinding[] = [];

  if (!parsed.header.baa) {
    findings.push({
      severity: 'error',
      path: `${txPath}.header.baa`,
      message:
        'Missing BAA segment — every 844 must open with a Beginning Segment for Product Transfer Account Adjustment.',
    });
  }

  if (parsed.nameLoops.length === 0) {
    findings.push({
      severity: 'error',
      path: `${txPath}.nameLoops`,
      message:
        'No N1 party loops found in this product transfer account adjustment.',
    });
  }

  if (parsed.items.length === 0) {
    findings.push({
      severity: 'error',
      path: `${txPath}.items`,
      message:
        'No PAD claim/adjustment lines found in this product transfer account adjustment.',
    });
  }

  for (const {segment, path} of collectParsedSegments844(parsed, txPath)) {
    checkElements(segment, path, findings);
  }

  return findings;
}

function validateParsed852(
  parsed: Parsed852,
  txPath: string
): ValidationFinding[] {
  const findings: ValidationFinding[] = [];

  if (!parsed.header.bct) {
    findings.push({
      severity: 'error',
      path: `${txPath}.header.bct`,
      message:
        'Missing BCT segment — every 852 must open with a Beginning Segment for Product Transfer and Resale Report.',
    });
  }

  if (parsed.items.length === 0) {
    findings.push({
      severity: 'error',
      path: `${txPath}.items`,
      message: 'No LIN item-activity lines found in this report.',
    });
  }

  if (!parsed.summary.ctt) {
    findings.push({
      severity: 'error',
      path: `${txPath}.summary.ctt`,
      message:
        'Missing CTT segment — the transaction has no summary line-item count.',
    });
  } else {
    const declared = elementValueAsString(parsed.summary.ctt.elements[0]);
    if (declared !== undefined && Number(declared) !== parsed.items.length) {
      findings.push({
        severity: 'error',
        path: `${txPath}.summary.ctt`,
        message: `CTT01 says ${declared} line item(s) but ${parsed.items.length} LIN line(s) were found.`,
      });
    }
  }

  for (const {segment, path} of collectParsedSegments852(parsed, txPath)) {
    checkElements(segment, path, findings);
  }

  return findings;
}

function collectNameLoopSegments855(
  loop: NameLoop855,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: loop.n1, path: `${path}.n1`}];
  if (loop.n2) out.push({segment: loop.n2, path: `${path}.n2`});
  if (loop.n3) out.push({segment: loop.n3, path: `${path}.n3`});
  if (loop.n4) out.push({segment: loop.n4, path: `${path}.n4`});
  if (loop.n5) out.push({segment: loop.n5, path: `${path}.n5`});
  if (loop.n6) out.push({segment: loop.n6, path: `${path}.n6`});
  loop.n9.forEach((s, i) => out.push({segment: s, path: `${path}.n9[${i}]`}));
  loop.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  if (loop.per) out.push({segment: loop.per, path: `${path}.per`});
  return out;
}

function collectItemAckSegments855(
  item: ItemAckLoop855,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: item.po1, path: `${path}.po1`}];
  if (item.ack) out.push({segment: item.ack, path: `${path}.ack`});
  item.pid.forEach((s, i) => out.push({segment: s, path: `${path}.pid[${i}]`}));
  item.mea.forEach((s, i) => out.push({segment: s, path: `${path}.mea[${i}]`}));
  item.pkg.forEach((s, i) => out.push({segment: s, path: `${path}.pkg[${i}]`}));
  if (item.po3) out.push({segment: item.po3, path: `${path}.po3`});
  if (item.ctp) out.push({segment: item.ctp, path: `${path}.ctp`});
  item.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  if (item.per) out.push({segment: item.per, path: `${path}.per`});
  item.sac.forEach((s, i) => out.push({segment: s, path: `${path}.sac[${i}]`}));
  item.sdq.forEach((s, i) => out.push({segment: s, path: `${path}.sdq[${i}]`}));
  item.sch.forEach((s, i) => out.push({segment: s, path: `${path}.sch[${i}]`}));
  item.dtm.forEach((s, i) => out.push({segment: s, path: `${path}.dtm[${i}]`}));
  item.nameSubLoops.forEach((sub, i) => {
    const subPath = `${path}.nameSubLoops[${i}]`;
    out.push({segment: sub.n1, path: `${subPath}.n1`});
    if (sub.n2) out.push({segment: sub.n2, path: `${subPath}.n2`});
    if (sub.n3) out.push({segment: sub.n3, path: `${subPath}.n3`});
    if (sub.n4) out.push({segment: sub.n4, path: `${subPath}.n4`});
  });
  return out;
}

function collectParsedSegments855(
  parsed: Parsed855,
  basePath: string
): PathedSegment[] {
  const out: PathedSegment[] = [];
  const {header} = parsed;
  const push = (segment: EdiSegment | undefined, suffix: string) => {
    if (segment) out.push({segment, path: `${basePath}.${suffix}`});
  };
  const pushAll = (segments: EdiSegment[], suffix: string) => {
    segments.forEach((s, i) =>
      out.push({segment: s, path: `${basePath}.${suffix}[${i}]`})
    );
  };

  push(header.bak, 'header.bak');
  push(header.cur, 'header.cur');
  pushAll(header.refs, 'header.refs');
  push(header.per, 'header.per');
  push(header.fob, 'header.fob');
  push(header.itd, 'header.itd');
  pushAll(header.dtm, 'header.dtm');
  push(header.td5, 'header.td5');

  parsed.nameLoops.forEach((loop, i) => {
    out.push(
      ...collectNameLoopSegments855(loop, `${basePath}.nameLoops[${i}]`)
    );
  });
  parsed.items.forEach((item, i) => {
    out.push(...collectItemAckSegments855(item, `${basePath}.items[${i}]`));
  });

  push(parsed.summary.ctt, 'summary.ctt');
  pushAll(parsed.summary.amt, 'summary.amt');

  return out;
}

function validateParsed855(
  parsed: Parsed855,
  txPath: string
): ValidationFinding[] {
  const findings: ValidationFinding[] = [];

  if (!parsed.header.bak) {
    findings.push({
      severity: 'error',
      path: `${txPath}.header.bak`,
      message:
        'Missing BAK segment — every 855 must open with a Beginning Segment for Purchase Order Acknowledgment.',
    });
  }

  if (parsed.items.length === 0) {
    findings.push({
      severity: 'error',
      path: `${txPath}.items`,
      message: 'No PO1 line items found in this purchase order acknowledgment.',
    });
  }

  if (!parsed.summary.ctt) {
    findings.push({
      severity: 'error',
      path: `${txPath}.summary.ctt`,
      message:
        'Missing CTT segment — the transaction has no summary line-item count.',
    });
  } else {
    const declared = elementValueAsString(parsed.summary.ctt.elements[0]);
    if (declared !== undefined && Number(declared) !== parsed.items.length) {
      findings.push({
        severity: 'error',
        path: `${txPath}.summary.ctt`,
        message: `CTT01 says ${declared} line item(s) but ${parsed.items.length} PO1 line(s) were found.`,
      });
    }
  }

  parsed.items.forEach((item, i) => {
    if (!item.ack) {
      findings.push({
        severity: 'error',
        path: `${txPath}.items[${i}].ack`,
        message:
          'Missing ACK segment — every PO1 line in an 855 should carry a Line Item Acknowledgment.',
      });
    }
  });

  for (const {segment, path} of collectParsedSegments855(parsed, txPath)) {
    checkElements(segment, path, findings);
  }

  return findings;
}

function collectNameLoopSegments810(
  loop: NameLoop810,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: loop.n1, path: `${path}.n1`}];
  if (loop.n2) out.push({segment: loop.n2, path: `${path}.n2`});
  if (loop.n3) out.push({segment: loop.n3, path: `${path}.n3`});
  if (loop.n4) out.push({segment: loop.n4, path: `${path}.n4`});
  if (loop.n5) out.push({segment: loop.n5, path: `${path}.n5`});
  if (loop.n6) out.push({segment: loop.n6, path: `${path}.n6`});
  loop.n9.forEach((s, i) => out.push({segment: s, path: `${path}.n9[${i}]`}));
  loop.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  if (loop.per) out.push({segment: loop.per, path: `${path}.per`});
  return out;
}

function collectItemSegments810(
  item: ItemLoop810,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: item.it1, path: `${path}.it1`}];
  item.pid.forEach((s, i) => out.push({segment: s, path: `${path}.pid[${i}]`}));
  item.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  item.sac.forEach((s, i) => out.push({segment: s, path: `${path}.sac[${i}]`}));
  item.txi.forEach((s, i) => out.push({segment: s, path: `${path}.txi[${i}]`}));
  item.dtm.forEach((s, i) => out.push({segment: s, path: `${path}.dtm[${i}]`}));
  return out;
}

function collectParsedSegments810(
  parsed: Parsed810,
  basePath: string
): PathedSegment[] {
  const out: PathedSegment[] = [];
  const {header} = parsed;
  const push = (segment: EdiSegment | undefined, suffix: string) => {
    if (segment) out.push({segment, path: `${basePath}.${suffix}`});
  };
  const pushAll = (segments: EdiSegment[], suffix: string) => {
    segments.forEach((s, i) =>
      out.push({segment: s, path: `${basePath}.${suffix}[${i}]`})
    );
  };

  push(header.big, 'header.big');
  push(header.cur, 'header.cur');
  pushAll(header.refs, 'header.refs');
  push(header.per, 'header.per');
  pushAll(header.pwk, 'header.pwk');
  pushAll(header.dtm, 'header.dtm');
  push(header.itd, 'header.itd');
  push(header.fob, 'header.fob');
  push(header.cad, 'header.cad');
  pushAll(header.msg, 'header.msg');
  pushAll(header.n9, 'header.n9');

  parsed.nameLoops.forEach((loop, i) => {
    out.push(
      ...collectNameLoopSegments810(loop, `${basePath}.nameLoops[${i}]`)
    );
  });
  parsed.items.forEach((item, i) => {
    out.push(...collectItemSegments810(item, `${basePath}.items[${i}]`));
  });

  push(parsed.summary.tds, 'summary.tds');
  pushAll(parsed.summary.sac, 'summary.sac');
  pushAll(parsed.summary.txi, 'summary.txi');
  push(parsed.summary.ctt, 'summary.ctt');

  return out;
}

function validateParsed810(
  parsed: Parsed810,
  txPath: string
): ValidationFinding[] {
  const findings: ValidationFinding[] = [];

  if (!parsed.header.big) {
    findings.push({
      severity: 'error',
      path: `${txPath}.header.big`,
      message:
        'Missing BIG segment — every 810 must open with a Beginning Segment for Invoice.',
    });
  }

  if (!parsed.summary.ctt) {
    findings.push({
      severity: 'error',
      path: `${txPath}.summary.ctt`,
      message:
        'Missing CTT segment — the transaction has no summary line-item count.',
    });
  } else {
    const declared = elementValueAsString(parsed.summary.ctt.elements[0]);
    if (declared !== undefined && Number(declared) !== parsed.items.length) {
      findings.push({
        severity: 'error',
        path: `${txPath}.summary.ctt`,
        message: `CTT01 says ${declared} line item(s) but ${parsed.items.length} IT1 line(s) were found.`,
      });
    }
  }

  for (const {segment, path} of collectParsedSegments810(parsed, txPath)) {
    checkElements(segment, path, findings);
  }

  return findings;
}

function collectNameLoopSegments856(
  loop: NameLoop856,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: loop.n1, path: `${path}.n1`}];
  if (loop.n2) out.push({segment: loop.n2, path: `${path}.n2`});
  if (loop.n3) out.push({segment: loop.n3, path: `${path}.n3`});
  if (loop.n4) out.push({segment: loop.n4, path: `${path}.n4`});
  if (loop.n5) out.push({segment: loop.n5, path: `${path}.n5`});
  if (loop.n6) out.push({segment: loop.n6, path: `${path}.n6`});
  return out;
}

function collectHLNodeSegments856(
  node: HLNode856,
  path: string
): PathedSegment[] {
  const out: PathedSegment[] = [{segment: node.hl, path: `${path}.hl`}];
  node.td1.forEach((s, i) => out.push({segment: s, path: `${path}.td1[${i}]`}));
  node.td5.forEach((s, i) => out.push({segment: s, path: `${path}.td5[${i}]`}));
  node.refs.forEach((s, i) =>
    out.push({segment: s, path: `${path}.refs[${i}]`})
  );
  if (node.per) out.push({segment: node.per, path: `${path}.per`});
  if (node.fob) out.push({segment: node.fob, path: `${path}.fob`});
  node.dtm.forEach((s, i) => out.push({segment: s, path: `${path}.dtm[${i}]`}));
  node.nameLoops.forEach((loop, i) => {
    out.push(...collectNameLoopSegments856(loop, `${path}.nameLoops[${i}]`));
  });
  if (node.prf) out.push({segment: node.prf, path: `${path}.prf`});
  node.man.forEach((s, i) => out.push({segment: s, path: `${path}.man[${i}]`}));
  if (node.lin) out.push({segment: node.lin, path: `${path}.lin`});
  if (node.sn1) out.push({segment: node.sn1, path: `${path}.sn1`});
  node.pid.forEach((s, i) => out.push({segment: s, path: `${path}.pid[${i}]`}));
  return out;
}

function collectHLTreeSegments856(
  nodes: HLNode856[],
  basePath: string
): PathedSegment[] {
  const out: PathedSegment[] = [];
  nodes.forEach((node, i) => {
    const path = `${basePath}[${i}]`;
    out.push(...collectHLNodeSegments856(node, path));
    out.push(...collectHLTreeSegments856(node.children, `${path}.children`));
  });
  return out;
}

function collectParsedSegments856(
  parsed: Parsed856,
  basePath: string
): PathedSegment[] {
  const out: PathedSegment[] = [];
  if (parsed.header.bsn) {
    out.push({segment: parsed.header.bsn, path: `${basePath}.header.bsn`});
  }
  out.push(
    ...collectHLTreeSegments856(parsed.shipments, `${basePath}.shipments`)
  );
  if (parsed.summary.ctt) {
    out.push({segment: parsed.summary.ctt, path: `${basePath}.summary.ctt`});
  }
  return out;
}

interface PathedHLNode856 {
  node: HLNode856;
  path: string;
}

function flattenHLNodes856(
  nodes: HLNode856[],
  basePath: string
): PathedHLNode856[] {
  const out: PathedHLNode856[] = [];
  nodes.forEach((node, i) => {
    const path = `${basePath}[${i}]`;
    out.push({node, path});
    out.push(...flattenHLNodes856(node.children, `${path}.children`));
  });
  return out;
}

/** Which HL03 level codes are legitimate under each BSN05 hierarchical
 * structure code — Tare is accepted anywhere Pack is, since the two sit as
 * siblings under the same parent. Only covers the structure codes the
 * requirements doc names explicitly; an unrecognized BSN05 value is flagged
 * separately below instead of being cross-checked against a guessed
 * structure. */
const BSN05_ALLOWED_LEVEL_CODES: Record<string, ReadonlySet<string>> = {
  '0001': new Set(['S', 'O', 'P', 'T', 'I']),
  '0002': new Set(['S', 'O', 'I', 'P', 'T']),
  '0004': new Set(['S', 'O', 'I']),
};

function validateParsed856(
  parsed: Parsed856,
  txPath: string
): ValidationFinding[] {
  const findings: ValidationFinding[] = [];

  if (!parsed.header.bsn) {
    findings.push({
      severity: 'error',
      path: `${txPath}.header.bsn`,
      message:
        'Missing BSN segment — every 856 must open with a Beginning Segment for Ship Notice.',
    });
  }

  if (parsed.shipments.length === 0) {
    findings.push({
      severity: 'error',
      path: `${txPath}.shipments`,
      message: 'No HL hierarchical loops found in this ship notice.',
    });
  }

  const allNodes = flattenHLNodes856(parsed.shipments, `${txPath}.shipments`);
  const itemCount = allNodes.filter(({node}) => node.levelCode === 'I').length;

  if (!parsed.summary.ctt) {
    findings.push({
      severity: 'error',
      path: `${txPath}.summary.ctt`,
      message:
        'Missing CTT segment — the transaction has no summary line-item count.',
    });
  } else {
    const declared = elementValueAsString(parsed.summary.ctt.elements[0]);
    if (declared !== undefined && Number(declared) !== itemCount) {
      findings.push({
        severity: 'error',
        path: `${txPath}.summary.ctt`,
        message: `CTT01 says ${declared} line item(s) but ${itemCount} Item (HL03="I") loop(s) were found.`,
      });
    }
  }

  parsed.shipments.forEach((node, i) => {
    if (node.parentId !== undefined) {
      findings.push({
        severity: 'error',
        path: `${txPath}.shipments[${i}].hl`,
        message: `HL${node.id}'s HL02 references hierarchical id "${node.parentId}", which doesn't exist earlier in this transaction.`,
      });
    }
  });

  const bsn05 = elementValueAsString(parsed.header.bsn?.elements[4])?.trim();
  const allowedLevels =
    bsn05 !== undefined ? BSN05_ALLOWED_LEVEL_CODES[bsn05] : undefined;
  if (allowedLevels) {
    for (const {node, path} of allNodes) {
      if (node.levelCode && !allowedLevels.has(node.levelCode)) {
        findings.push({
          severity: 'warning',
          path: `${path}.hl`,
          message: `HL${node.id} is an HL03="${node.levelCode}" level, which isn't part of the Shipment/Order/Pack/Tare/Item structure BSN05="${bsn05}" declares.`,
        });
      }
    }
  } else if (bsn05 !== undefined) {
    findings.push({
      severity: 'warning',
      path: `${txPath}.header.bsn`,
      message: `BSN05="${bsn05}" is a hierarchical structure code this tool doesn't recognize, so the Pack/Tare/Item structure wasn't cross-checked against it.`,
    });
  }

  for (const {segment, path} of collectParsedSegments856(parsed, txPath)) {
    checkElements(segment, path, findings);
  }

  return findings;
}

/**
 * Validates an envelope (with parsed850/parsed852/parsed856 attached by
 * lib/explainedEnvelope.ts) and returns a flat, advisory-only list of
 * findings. Never throws, and — deliberately — has no return value or code
 * path that represents "export is blocked".
 */
export function validate(envelope: EdiEnvelopeExplained): ValidationFinding[] {
  const findings: ValidationFinding[] = [];

  if (!envelope.isaSegment) {
    findings.push({
      severity: 'error',
      path: 'isaSegment',
      message:
        'Missing ISA segment — this does not look like a complete X12 interchange.',
    });
  } else {
    checkIsaPadding(envelope.isaSegment, findings);
  }
  if (!envelope.ieaSegment) {
    findings.push({
      severity: 'error',
      path: 'ieaSegment',
      message:
        'Missing IEA segment — the interchange envelope was never closed.',
    });
  }

  envelope.groups.forEach((group, gi) => {
    const groupPath = `groups[${gi}]`;

    if (!group.gsSegment) {
      findings.push({
        severity: 'error',
        path: `${groupPath}.gsSegment`,
        message:
          'A transaction set appears with no enclosing GS functional group.',
      });
    }

    if (!group.geSegment) {
      findings.push({
        severity: 'error',
        path: `${groupPath}.geSegment`,
        message: 'Missing GE segment — this functional group was never closed.',
      });
    } else {
      const declared = elementValueAsString(group.geSegment.elements[0]);
      const actual = group.transactions.length;
      if (declared !== undefined && Number(declared) !== actual) {
        findings.push({
          severity: 'error',
          path: `${groupPath}.geSegment`,
          message: `GE01 says ${declared} transaction set(s) but ${actual} were found.`,
        });
      }
    }

    group.transactions.forEach((transaction, ti) => {
      const txPath = `${groupPath}.transactions[${ti}]`;

      if (
        transaction.transactionSetCode !== '810' &&
        transaction.transactionSetCode !== '840' &&
        transaction.transactionSetCode !== '850' &&
        transaction.transactionSetCode !== '852' &&
        transaction.transactionSetCode !== '855' &&
        transaction.transactionSetCode !== '843' &&
        transaction.transactionSetCode !== '856' &&
        transaction.transactionSetCode !== '997' &&
        transaction.transactionSetCode !== '844' &&
        transaction.transactionSetCode !== '845' &&
        transaction.transactionSetCode !== '867' &&
        transaction.transactionSetCode !== '210'
      ) {
        findings.push({
          severity: 'warning',
          path: `${txPath}.stSegment`,
          message: `Transaction set type "${transaction.transactionSetCode}" is not an 810, 840, 843, 844, 845, 850, 852, 855, 856, 867, 997 or 210 — shown read-only.`,
        });
      }

      if (!transaction.seSegment) {
        findings.push({
          severity: 'error',
          path: `${txPath}.seSegment`,
          message:
            'Missing SE segment — this transaction set was never closed.',
        });
      } else {
        const declared = elementValueAsString(
          transaction.seSegment.elements[0]
        );
        const actual = transaction.segments.length + 2; // + ST and SE themselves
        if (declared !== undefined && Number(declared) !== actual) {
          findings.push({
            severity: 'error',
            path: `${txPath}.seSegment`,
            message: `SE01 says ${declared} segment(s) but ${actual} were found (including ST and SE).`,
          });
        }
      }

      if (transaction.parsed840) {
        findings.push(...validateParsed840(transaction.parsed840, txPath));
      } else if (transaction.parsed850) {
        findings.push(...validateParsed850(transaction.parsed850, txPath));
      } else if (transaction.parsed852) {
        findings.push(...validateParsed852(transaction.parsed852, txPath));
      } else if (transaction.parsed855) {
        findings.push(...validateParsed855(transaction.parsed855, txPath));
      } else if (transaction.parsed856) {
        findings.push(...validateParsed856(transaction.parsed856, txPath));
      } else if (transaction.parsed997) {
        findings.push(...validateParsed997(transaction.parsed997, txPath));
      } else if (transaction.parsed810) {
        findings.push(...validateParsed810(transaction.parsed810, txPath));
      } else if (transaction.parsed843) {
        findings.push(...validateParsed843(transaction.parsed843, txPath));
      } else if (transaction.parsed844) {
        findings.push(...validateParsed844(transaction.parsed844, txPath));
      } else if (transaction.parsed845) {
        findings.push(...validateParsed845(transaction.parsed845, txPath));
      } else if (transaction.parsed867) {
        findings.push(...validateParsed867(transaction.parsed867, txPath));
      } else if (transaction.parsed210) {
        findings.push(...validateParsed210(transaction.parsed210, txPath));
      }
    });
  });

  return findings;
}
